/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-template-curly-in-string */
import React, { useState, useEffect, useCallback } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  AutoComplete,
  Select,
  Spin,
  Switch,
} from "antd";
import { PlusOutlined, LoadingOutlined, DeleteFilled } from "@ant-design/icons";
import { debounce } from "lodash";

import {
  CompactIntro,
  V2Form,
  TwoPaneLayout,
  LeftPane,
  RightPane,
  V2AddCategoryPanel,
  V2AddCategoryActions,
  CategoryListHeader,
  CategoryList,
  CategoryCard,
  CategoryCardContent,
  CategoryName,
  MetadataGrid,
  V2ActionsBar,
  StyledButton,
  CategoryMetadata,
} from "./StyledComponents";

const { Option } = Select;
const { Text } = Typography;

const StateForm = ({
  mode,
  form,
  fields,
  onFieldsChange,
  onFinish,
  states,
  telemedicineCategory,
  consultationType,
  listOfPharmacies,
  fetchPharmacies,
  pharmacyLoading,
  addedTeleMedicineCategory,
  setAddedTeleMedicineCategory,
  onCategoryAdd,
  onCategoryRemove,
  renderBulkPharmacyUpdateControls,
  onCancel,
}) => {
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
  const [
    teleMedicineCategoryForSelection,
    setTeleMedicineCategoryForSelection,
  ] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [selectedConsultation, setSelectedConsultation] = useState({});
  const [selectedRenewalConsultation, setSelectedRenewalConsultation] = useState({});
  const [
    addedTelemedicineCategoryValidatedStatus,
    setAddedTelemedicineCategoryValidatedStatus,
  ] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState({});
  const [selectPharmacySearchValue, setSelectPharmacySearchValue] = useState(
    ""
  );

  const isEdit = mode === "edit";
  const idPrefix = isEdit ? "edit-state" : "add-state";

  const handlePharmacySearch = (value) => {
    setSelectPharmacySearchValue(value);
  };

  const searchPharmacy = (searchPharmacyValue) => {
    fetchPharmacies(searchPharmacyValue);
  };

  const debounceSearchPharmacies = useCallback(
    debounce(searchPharmacy, 1250),
    []
  );

  useEffect(() => {
    debounceSearchPharmacies(selectPharmacySearchValue);
  }, [selectPharmacySearchValue]);

  const handlePharmacySelectionChange = (value) => {
    setSelectedPharmacy(value);
  };

  const updateTeleMedicineCategoryForSelection = () => {
    const checkAddedTeleMedicineCategory = (accumulator, currentCategory) => {
      const foundTelemedicineCategory = addedTeleMedicineCategory.find(
        (eachSelectedCategory) =>
          currentCategory.key === eachSelectedCategory.categoryId
      );
      if (!foundTelemedicineCategory) {
        accumulator.push(currentCategory);
      }
      return accumulator;
    };
    const teleMedicineCategoryForSelection = telemedicineCategory.reduce(
      checkAddedTeleMedicineCategory,
      []
    );
    setTeleMedicineCategoryForSelection(teleMedicineCategoryForSelection);
  };

  useEffect(() => {
    updateTeleMedicineCategoryForSelection();
  }, [telemedicineCategory, addedTeleMedicineCategory]);

  const onConsultationTypeChange = (value) => {
    setSelectedConsultation(value);
  };

  const onRenewalConsultationTypeChange = (value) => {
    setSelectedRenewalConsultation(value);
  };

  const onTeleMedicineCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const handleDaysAvailableAddition = () => {
    const selectedTelemedicineCategory = {
      categoryId: selectedCategory.value,
      pharmacyId: selectedPharmacy.value,
      pharmacyName: selectedPharmacy.label,
      categoryName: selectedCategory.label,
      consultationType: selectedConsultation.value,
      ...(isEdit ? { renewalConsultationType: selectedRenewalConsultation.value } : {}),
    };
    const clonnedSelectedTelemedicineCategory = addedTeleMedicineCategory.slice(
      0
    );
    clonnedSelectedTelemedicineCategory.push(selectedTelemedicineCategory);
    setAddedTeleMedicineCategory(clonnedSelectedTelemedicineCategory);
    setSelectedCategory({});
    setSelectedConsultation({});
    setSelectedRenewalConsultation({});
    setSelectedPharmacy({});

    if (onCategoryAdd) {
      onCategoryAdd(selectedCategory.value, selectedTelemedicineCategory);
    }

    if (addedTelemedicineCategoryValidatedStatus === "error") {
      setAddedTelemedicineCategoryValidatedStatus("success");
    }
  };

  const handleTeleMedicineCategoryDelete = (itemIndex) => {
    const clonnedTelemedicineCategory = addedTeleMedicineCategory.slice(0);
    const [category] = clonnedTelemedicineCategory.splice(itemIndex, 1);

    if (onCategoryRemove) {
      onCategoryRemove(category, itemIndex);
    }

    if (!clonnedTelemedicineCategory.length) {
      setAddedTelemedicineCategoryValidatedStatus("error");
    }
    setAddedTeleMedicineCategory(clonnedTelemedicineCategory);
  };

  const updateAutoCompleteOptions = () => {
    const options = states.map((eachState) => ({
      key: eachState._id,
      value: eachState.name,
    }));
    setAutoCompleteOptions(options);
  };

  useEffect(() => {
    updateAutoCompleteOptions();
  }, [states]);

  const handleFinish = (values) => {
    if (!addedTeleMedicineCategory.length) {
      setAddedTelemedicineCategoryValidatedStatus("error");
      return false;
    }

    setAddedTelemedicineCategoryValidatedStatus("");
    return onFinish(values);
  };

  const layout = {
    labelCol: {
      span: 12,
    },
    wrapperCol: {
      span: 24,
    },
  };
  const validateMessages = {
    required: "${label} is required!",
  };

  const stateName = isEdit ? "stateName" : ["state", "stateName"];
  const stateNamePlaceholder = isEdit ? "State Name" : "Enter State Name";
  const consultationLabel = isEdit ? "Initial Consultation" : "Consultation Type";
  const consultationSelectId = isEdit
    ? "edit-state-initial-consultation-select"
    : "add-state-consultation-type-select";
  const addCategoryDisabled = isEdit
    ? !(Object.keys(selectedCategory).length && Object.keys(selectedConsultation).length)
    : !(Object.keys(selectedCategory).length && Object.keys(selectedConsultation).length && selectedPharmacy);
  const categoryCardKeyPrefix = isEdit ? "edit-category-v2" : "add-category-v2";

  return (
    <V2Form
      {...layout}
      form={form}
      layout="vertical"
      name="nest-messages"
      onFinish={handleFinish}
      fields={fields}
      onFieldsChange={onFieldsChange}
      validateMessages={validateMessages}
    >
      <TwoPaneLayout>
        <LeftPane>
          <Row gutter={8} align="middle" justify="space-between">
            <Col xs={24} md={14}>
              <Form.Item name={stateName} label="State Name" rules={[{ required: true }]}>
                <AutoComplete
                  id={`${idPrefix}-name-autocomplete`}
                  options={autoCompleteOptions}
                  disabled={isEdit}
                  filterOption={(inputValue, option) =>
                    option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                >
                  <Input placeholder={stateNamePlaceholder} disabled={isEdit} />
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                name="oneClickPrescribeEligible"
                label="CEV eligible"
                valuePropName="checked"
              >
                <Switch id={`${idPrefix}-one-click-prescribe-switch`} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="pdmpResource" label="PDMP Resource (URL)">
                <Input
                  id={`${idPrefix}-pdmp-resource-input`}
                  placeholder="Controlled Substances PDMP Resource"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            shouldUpdate={() => addedTeleMedicineCategory.length}
            name="telemedicine-consultation-mapping"
            validateStatus={addedTelemedicineCategoryValidatedStatus}
            help={
              addedTelemedicineCategoryValidatedStatus === "error"
                ? "Please select at least one Telemedicine Category, Consultation Type and pharmacy, then add it using `+` button"
                : ""
            }
            rules={[
              ({ getFieldValue }) => ({
                validator() {
                  if (addedTeleMedicineCategory.length) {
                    return Promise.resolve();
                  }

                  setAddedTelemedicineCategoryValidatedStatus("error");
                  return Promise.reject(
                    "Please select at least one Telemedicine Category, Consultation Type and pharmacy, then add it using `+` button"
                  );
                },
              }),
            ]}
          >
            <V2AddCategoryPanel>
              <CompactIntro>Select category details, then add them to the state.</CompactIntro>
              <Row gutter={[8, 0]} align="bottom">
                <Col xs={24}>
                  <Form.Item label="Telemedicine Category">
                    <Select
                      id={`${idPrefix}-telemedicine-category-select`}
                      placeholder="Select a Telemedicine Category"
                      showSearch
                      labelInValue
                      value={selectedCategory}
                      onChange={onTeleMedicineCategoryChange}
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {teleMedicineCategoryForSelection.map((eachCategory) => (
                        <Option key={eachCategory._id} value={eachCategory._id}>
                          {eachCategory.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={isEdit ? 12 : undefined}>
                  <Form.Item label={consultationLabel}>
                    <Select
                      id={consultationSelectId}
                      labelInValue
                      value={selectedConsultation}
                      placeholder="Select Consultation Type"
                      onChange={onConsultationTypeChange}
                    >
                      {consultationType.map((eachType) => (
                        <Option key={eachType.key} value={eachType.value}>
                          {eachType.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                {isEdit && (
                  <Col xs={24} md={12}>
                    <Form.Item label="Renewal Consultation">
                      <Select
                        id="edit-state-renewal-consultation-select"
                        labelInValue
                        value={selectedRenewalConsultation}
                        placeholder="Select Consultation Type"
                        onChange={onRenewalConsultationTypeChange}
                      >
                        {consultationType.map((eachType) => (
                          <Option key={eachType.key} value={eachType.value}>
                            {eachType.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24}>
                  <Form.Item label="Pharmacy">
                    <Spin spinning={pharmacyLoading} size="small" indicator={<LoadingOutlined />}>
                      <Select
                        id={`${idPrefix}-pharmacy-select`}
                        showSearch
                        value={selectedPharmacy}
                        placeholder="Enter Pharmacy Name"
                        defaultActiveFirstOption={false}
                        showArrow={false}
                        filterOption={false}
                        onSearch={handlePharmacySearch}
                        onChange={handlePharmacySelectionChange}
                        notFoundContent={null}
                        labelInValue
                      >
                        {listOfPharmacies.map((eachPharmacy) => (
                          <Option key={eachPharmacy.key} value={eachPharmacy.value}>
                            {eachPharmacy.name}
                          </Option>
                        ))}
                      </Select>
                    </Spin>
                  </Form.Item>
                  {renderBulkPharmacyUpdateControls && renderBulkPharmacyUpdateControls({ form })}
                </Col>
                <Col xs={24}>
                  <V2AddCategoryActions>
                    <Button
                      id={`${idPrefix}-add-category-btn`}
                      type="primary"
                      disabled={addCategoryDisabled}
                      icon={<PlusOutlined />}
                      onClick={handleDaysAvailableAddition}
                    >
                      Add Category
                    </Button>
                  </V2AddCategoryActions>
                </Col>
              </Row>
            </V2AddCategoryPanel>
          </Form.Item>
        </LeftPane>

        <RightPane>
          <CategoryListHeader>
            <Text strong>Added Telemedicine Categories</Text>
            <Text type="secondary">{addedTeleMedicineCategory.length} selected</Text>
          </CategoryListHeader>
          <CategoryList>
            {addedTeleMedicineCategory.length ? (
              addedTeleMedicineCategory.map((category, index) => (
                <CategoryCard key={category.key || category.categoryId || `${categoryCardKeyPrefix}-${index}`}>
                  <CategoryCardContent>
                    <CategoryName strong>{category.categoryName || "Telemedicine Category"}</CategoryName>
                    <MetadataGrid>
                      {isEdit ? (
                        <>
                          <CategoryMetadata label="Initial consultation" value={category.consultationType} />
                          <CategoryMetadata label="Renewal consultation" value={category.renewalConsultationType} />
                        </>
                      ) : (
                        <CategoryMetadata label="Consultation" value={category.consultationType} />
                      )}
                      <CategoryMetadata label="Pharmacy" value={category.pharmacyName} />
                    </MetadataGrid>
                  </CategoryCardContent>
                  <Button
                    aria-label={`Remove ${category.categoryName || "telemedicine category"}`}
                    shape="circle"
                    icon={<DeleteFilled />}
                    onClick={() => handleTeleMedicineCategoryDelete(index)}
                  />
                </CategoryCard>
              ))
            ) : (
              <Text type="secondary">No telemedicine categories selected</Text>
            )}
          </CategoryList>
        </RightPane>
      </TwoPaneLayout>

      <V2ActionsBar>
        <StyledButton id={`${idPrefix}-cancel-btn`} type="secondary" size="large" onClick={onCancel}>
          Cancel
        </StyledButton>
        <StyledButton
          id={isEdit ? "edit-state-save-btn" : "add-state-submit-btn"}
          type="primary"
          size="large"
          htmlType="submit"
        >
          {isEdit ? "Save" : "Add"}
        </StyledButton>
      </V2ActionsBar>
    </V2Form>
  );
};

export default StateForm;
